import React from 'react';
import type { Location, SpecialCard } from '../api';
import { Card, StackedItemWithKind } from './Card';
import { useMonsterTokenStyle } from '../utils/clientSettings';
import { getCardBackPath } from '../utils/cardBackArtwork';

const specialCardLocations: Record<string, string> = {
  'coded messages': 'Newspaper',
  "velma's gratitude": "Velma's Diner",
  'rare book collection': 'Library',
};

type LocationCardsProps = { location: Location; specialCardsInPlay?: SpecialCard[] };

export function locationCards(location: Location, specialCardsInPlay: SpecialCard[] = []): (StackedItemWithKind | null)[] {
  const seen = new Set<string>();
  const specialCards = specialCardsInPlay.filter(card => {
    const name = card.name.trim().toLowerCase();
    if (specialCardLocations[name] !== location.name || seen.has(name)) return false;
    seen.add(name);
    return true;
  });
  return [
    ...(location.placedCommonItems ?? []).map(item => ({ item, kind: 'common' as const })),
    ...(location.placedUniqueItems ?? []).map(item => ({ item, kind: 'unique' as const })),
    ...Array.from({ length: location.facedownExhibitItemCount ?? 0 }, () => null),
    ...specialCards.map(item => ({ item, kind: 'special' as const })),
  ];
}

function PlacedCards({ cards }: { cards: (StackedItemWithKind | null)[] }) {
  const artwork = useMonsterTokenStyle();
  return <>{cards.map((card, index) => <div className="location-card" key={`${card?.kind}-${card?.item.name}-${index}`}>
    {card ? <Card card={card} /> : <div className={`card location-card-back${artwork === 'Digital' ? ' digital-card-back' : ''}`}><img src={getCardBackPath('exhibits', artwork)} alt="Facedown Exhibit Item" draggable={false} /></div>}
  </div>)}</>;
}

export function LocationCardsSection({ location, specialCardsInPlay }: LocationCardsProps) {
  const cards = locationCards(location, specialCardsInPlay);
  return cards.length ? <section className="digital-location-section" aria-label="Cards in location">
    <h3>Cards in location</h3><div className="digital-location-cards"><PlacedCards cards={cards} /></div>
  </section> : null;
}

export function SpecialEncounterCards({ location }: { location: Location }) {
  const info = location.specialEncounterInfo;
  if (!info) return null;
  return <>
    {!info.isAvailable && <p className="digital-location-unavailable">Unavailable: {info.unavailableReason || 'This Special Encounter is currently unavailable.'}</p>}
    {!!info.cards?.length && <div className="digital-location-cards" aria-label="Special Encounter cards">
      {info.cards.map(preview => <div className="location-card" key={`${preview.kind}-${preview.card.name}`}>
        <Card card={{ kind: preview.kind, item: preview.card }} />
        {preview.remainingCount !== undefined && <p className="digital-location-card-count">{preview.card.name}: {preview.remainingCount} remaining in deck</p>}
      </div>)}
    </div>}
  </>;
}

export function MapLocationCards({ location, specialCardsInPlay, right, frameHeight, availableWidth, onInspect }: {
  right: number; frameHeight: number; availableWidth: number; onInspect: () => void;
} & LocationCardsProps) {
  const artwork = useMonsterTokenStyle();
  const cards = locationCards(location, specialCardsInPlay);
  if (!cards.length) return null;
  const width = artwork === 'Digital' ? 200 : 160;
  const height = artwork === 'Digital' ? 300 : 260;
  const rowWidth = cards.length * width + (cards.length - 1) * 8;
  const scale = Math.min(1, frameHeight / height, Math.max(0, availableWidth) / rowWidth);
  return <div className="digital-map-location-cards" style={{ left: right - 20, top: -height * scale / 2, width: rowWidth * scale, height: height * scale }}>
    <div inert aria-hidden="true" className="digital-map-location-cards-content" style={{ width: rowWidth, height, transform: `scale(${scale})` }}>
      <PlacedCards cards={cards} />
    </div>
    <button type="button" className="digital-map-location-cards-inspect" aria-label={`Cards at ${location.name}`} aria-haspopup="dialog"
      onClick={event => { event.stopPropagation(); onInspect(); }} />
  </div>;
}
